import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, CalendarClock, Gavel, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatDays, formatMinutes, formatMoney, formatMonth, formatNumber } from '../lib/format';
import {
  SHORTFALL_TONE,
  type MyShortfall,
  type ShortfallDetail,
  type ShortfallRow,
  type ShortfallStatus,
} from '../lib/shortfall';
import { formatWorkDate } from './Violations';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Td,
  Th,
  Tr,
  statusTone,
} from '../components/ui';

type Filter = 'PENDING' | 'RESOLVED' | 'ALL';

/** "October 2026" from "2026-10". */
function monthLabel(effectiveMonth: string, locale: 'en' | 'ar'): string {
  const [year, month] = effectiveMonth.split('-').map(Number);
  return formatMonth(year, month, locale);
}

/** What a case's decision is, in a few words: the percentage of a day's pay, or the day of leave. */
export function ShortfallDecisionText({ row }: { row: { status: ShortfallStatus; deductionPercent: number | null } }) {
  const { t } = useI18n();
  if (row.status === 'RESOLVED_SALARY' && row.deductionPercent != null) {
    return <>{t('shortfall.decision.salary', { percent: row.deductionPercent })}</>;
  }
  if (row.status === 'RESOLVED_LEAVE') return <>{t('shortfall.decision.leave')}</>;
  if (row.status === 'RESOLVED_EXCUSED') return <>{t('shortfall.decision.excused')}</>;
  return <>{t('shortfall.status.PENDING_ADMIN_REVIEW')}</>;
}

/**
 * Violations & Penalties › Short days (`?tab=shortfall`) — the administrators'
 * queue of days worked more than an hour short (CR-004): waiting first, oldest
 * first; by state, employee and dates. A row opens its case.
 */
export function ShortfallQueue() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<Filter>('PENDING');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['shortfalls', 'list', filter, q, from, to, page],
    queryFn: () =>
      api.get<Paginated<ShortfallRow>>('/attendance-shortfalls', {
        status: filter,
        page,
        pageSize: 25,
        ...(q.trim() ? { q: q.trim() } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      }),
  });

  const reset = (apply: () => void) => {
    apply();
    setPage(1);
  };

  return (
    <>
      <p className="mb-3 max-w-3xl text-body-sm text-muted">{t('shortfall.queueNote')}</p>
      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Select value={filter} onChange={(e) => reset(() => setFilter(e.target.value as Filter))} aria-label={t('common.status')}>
            {(['PENDING', 'RESOLVED', 'ALL'] as const).map((f) => (
              <option key={f} value={f}>
                {t(`shortfall.filter.${f}` as TranslationKey)}
              </option>
            ))}
          </Select>
          <Input
            type="search"
            value={q}
            placeholder={t('shortfall.search')}
            aria-label={t('shortfall.search')}
            onChange={(e) => reset(() => setQ(e.target.value))}
          />
          <Input type="date" value={from} max={to || undefined} aria-label={t('shortfall.from')} onChange={(e) => reset(() => setFrom(e.target.value))} />
          <Input type="date" value={to} min={from || undefined} aria-label={t('shortfall.to')} onChange={(e) => reset(() => setTo(e.target.value))} />
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<CalendarClock className="h-8 w-8" />}
            title={filter === 'PENDING' ? t('shortfall.nothingWaiting') : t('shortfall.none')}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('nav.employees')}</Th>
                  <Th>{t('shortfall.workDate')}</Th>
                  <Th align="end">{t('shortfall.required')}</Th>
                  <Th align="end">{t('shortfall.worked')}</Th>
                  <Th align="end">{t('shortfall.missing')}</Th>
                  <Th>{t('shortfall.decisionCol')}</Th>
                  <Th align="end">{t('common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <Tr key={row.id} onClick={() => navigate(`/violations/short-days/${row.id}`)}>
                    <Td>
                      <span className="block">{localized(row.user)}</span>
                      {row.user.employeeCode && <span className="font-mono text-mono-sm text-faint">{row.user.employeeCode}</span>}
                    </Td>
                    <Td className="whitespace-nowrap tabular">{formatWorkDate(row.workDate, locale)}</Td>
                    <Td align="end" className="whitespace-nowrap tabular text-muted">{formatMinutes(row.requiredMinutes, locale)}</Td>
                    <Td align="end" className="whitespace-nowrap tabular">{formatMinutes(row.workedMinutes, locale)}</Td>
                    <Td align="end" className="whitespace-nowrap tabular font-medium text-error-fg dark:text-error-dark">
                      {formatMinutes(row.shortfallMinutes, locale)}
                    </Td>
                    <Td>
                      <Badge tone={SHORTFALL_TONE[row.status]}>{t(`shortfall.status.${row.status}` as TranslationKey)}</Badge>
                      {row.status !== 'PENDING_ADMIN_REVIEW' && (
                        <span className="mt-0.5 block text-caption text-muted">
                          <ShortfallDecisionText row={row} />
                          {row.decidedBy && ` · ${t('violations.by', { name: localized(row.decidedBy) })}`}
                        </span>
                      )}
                    </Td>
                    <Td align="end" className="whitespace-nowrap">
                      <span onClick={(e) => e.stopPropagation()}>
                        {row.can.resolve && (
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/violations/short-days/${row.id}/decide`)}>
                            {t('shortfall.decide')}
                          </Button>
                        )}
                      </span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

/**
 * The signed-in employee's own days more than an hour short, on top of their
 * penalties: one waiting says plainly that nothing is deducted until an
 * administrator decides (CR-004 §3.4). Nothing shows for someone with none.
 */
export function MyShortDays() {
  const { t, locale } = useI18n();
  const { data } = useQuery({
    queryKey: ['shortfalls', 'mine'],
    queryFn: () => api.get<MyShortfall[]>('/attendance-shortfalls/mine'),
  });
  if (!data?.length) return null;
  return (
    <Card className="mb-4">
      <CardHeader title={t('shortfall.mineTitle')} />
      <ul className="divide-y divide-[var(--line)]">
        {data.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2.5">
            <div className="min-w-0">
              <span className="tabular font-medium text-[var(--text-primary)]">{formatWorkDate(c.workDate, locale)}</span>
              <span className="ms-2 text-caption text-muted tabular">
                {t('violations.shortDayWorked', {
                  worked: formatMinutes(c.workedMinutes, locale),
                  owed: formatMinutes(c.requiredMinutes, locale),
                })}
              </span>
            </div>
            <div className="text-body-sm">
              {c.status === 'PENDING_ADMIN_REVIEW' ? (
                <span className="text-warning-fg dark:text-warning-dark">{t('shortfall.minePending')}</span>
              ) : c.status === 'RESOLVED_SALARY' ? (
                <span>
                  {t('shortfall.mineSalary', { percent: c.deductionPercent ?? 0 })}
                  {c.penaltyId && (
                    <Link to={`/violations/penalties/${c.penaltyId}`} className="ms-2 text-primary-600 hover:underline dark:text-primary-400">
                      {t('shortfall.openPenalty')}
                    </Link>
                  )}
                </span>
              ) : c.status === 'RESOLVED_EXCUSED' ? (
                <span className="text-success-fg dark:text-success-dark">{t('shortfall.mineExcused')}</span>
              ) : (
                <span>{t('shortfall.mineLeave')}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Route: `/violations/short-days/:id` — one day more than an hour short, for an
 * administrator: the day, the decision in force and what became of what it
 * posted, its history, and Decide or Reverse. Its notices open here.
 */
export function ShortfallDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: row, isLoading, error, refetch } = useQuery({
    queryKey: ['shortfalls', 'one', id],
    queryFn: () => api.get<ShortfallDetail>(`/attendance-shortfalls/${id}`),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !row) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const own = row.user.id === user?.id;
  const penalty = row.penalty;
  const leave = row.options.leave;

  return (
    <>
      <div className="mb-4">
        <Link
          to="/violations?tab=shortfall"
          className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('shortfall.tab')}
        </Link>
      </div>

      <PageHeader
        title={t('shortfall.title')}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge tone={SHORTFALL_TONE[row.status]}>{t(`shortfall.status.${row.status}` as TranslationKey)}</Badge>
            <span>{localized(row.user)}</span>
            <span className="tabular">{formatWorkDate(row.workDate, locale)}</span>
          </span>
        }
        action={
          <>
            {row.can.resolve && (
              <Button variant="primary" icon={<Gavel className="h-4 w-4" />} onClick={() => navigate(`/violations/short-days/${row.id}/decide`)}>
                {t('shortfall.decide')}
              </Button>
            )}
            {row.can.reverse && (
              <Button icon={<Undo2 className="h-4 w-4 flip-rtl" />} onClick={() => navigate(`/violations/short-days/${row.id}/reverse`)}>
                {t('shortfall.reverse')}
              </Button>
            )}
          </>
        }
      />

      {own && (
        <div className="mb-4 rounded-md border border-warning/40 bg-warning/8 px-4 py-2.5 text-body-sm text-warning-fg dark:text-warning-dark">
          {t('shortfall.ownDay')}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="min-w-0 space-y-5 lg:col-span-2">
          <Card>
            <CardHeader title={t('shortfall.theDay')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('rewards.employee')}>{localized(row.user)}</Detail>
              <Detail label={t('shortfall.workDate')}>
                <span className="tabular">{formatWorkDate(row.workDate, locale)}</span>
              </Detail>
              <Detail label={t('shortfall.required')}>
                <span className="tabular" title={row.hoursSource === 'DEFAULT' ? t('violations.owedFromDefault') : undefined}>
                  {formatMinutes(row.requiredMinutes, locale)}
                </span>
              </Detail>
              <Detail label={t('shortfall.worked')}>
                <span className="tabular">{formatMinutes(row.workedMinutes, locale)}</span>
              </Detail>
              <Detail label={t('shortfall.missing')}>
                <span className="tabular font-medium text-error-fg dark:text-error-dark">{formatMinutes(row.shortfallMinutes, locale)}</span>
              </Detail>
            </dl>
            <p className="mt-3 text-caption text-faint">{t('shortfall.rule', { minutes: formatMinutes(row.thresholdMinutes, locale) })}</p>
          </Card>

          <Card>
            <CardHeader title={t('shortfall.inForce')} />
            {row.status === 'PENDING_ADMIN_REVIEW' ? (
              <p className="text-body-sm text-muted">{t('shortfall.noDecision')}</p>
            ) : (
              <dl className="divide-y divide-[var(--line)]">
                <Detail label={t('shortfall.decisionCol')}>
                  <span className="font-medium">
                    <ShortfallDecisionText row={row} />
                  </span>
                  {row.deductionDays != null && <span className="ms-1.5 tabular text-muted">({formatDays(row.deductionDays, locale)})</span>}
                  {row.decidedBy && row.decidedAt && (
                    <span className="mt-0.5 block text-caption text-muted">
                      {t('shortfall.decidedBy', { name: localized(row.decidedBy), date: formatDateTime(row.decidedAt, locale) })}
                    </span>
                  )}
                </Detail>
                {penalty && (
                  <Detail label={t('shortfall.posting')}>
                    <span className="block">
                      <Badge tone={statusTone(penalty.status)}>{enumLabel(penalty.status, locale)}</Badge>
                      {penalty.amount != null && penalty.currency && (
                        <span className="ms-2 tabular text-muted">≈ {formatMoney(penalty.amount, penalty.currency, locale)}</span>
                      )}
                    </span>
                    <span className="mt-0.5 block text-caption text-muted">
                      {penalty.status === 'CANCELLED'
                        ? penalty.walletPoints != null
                          ? t('shortfall.penaltyWallet', { points: formatNumber(penalty.walletPoints, locale) })
                          : t('shortfall.penaltyCancelled', { reason: penalty.cancellationReason ?? '—' })
                        : penalty.onPayroll
                          ? t('shortfall.penaltyOnPayroll', { month: monthLabel(penalty.effectiveMonth, locale) })
                          : t('shortfall.penaltyWaiting', { month: monthLabel(penalty.effectiveMonth, locale) })}
                    </span>
                    <Link to={`/violations/penalties/${penalty.id}`} className="text-caption text-primary-600 hover:underline dark:text-primary-400">
                      {t('shortfall.openPenalty')}
                    </Link>
                  </Detail>
                )}
                {row.leaveGrant && (
                  <Detail label={t('shortfall.posting')}>
                    {t('shortfall.leaveTaken', { type: localized(row.leaveGrant.leaveType), year: String(row.leaveGrant.year) })}
                  </Detail>
                )}
              </dl>
            )}
            {row.decisionNote && (
              <div className="mt-4 border-t hairline pt-4">
                <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">{t('shortfall.note')}</p>
                <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                  {row.decisionNote}
                </p>
              </div>
            )}
          </Card>

          <Card padded={false}>
            <CardHeader title={t('shortfall.history')} className="p-5 pb-0" />
            {row.actions.length === 0 ? (
              <p className="p-5 text-body-sm text-muted">{t('shortfall.noHistory')}</p>
            ) : (
              <ol className="divide-y divide-[var(--line)]">
                {row.actions.map((a) => (
                  <li key={a.id} className="px-5 py-3">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <Badge tone={a.kind === 'RESOLVED' ? 'brand' : 'neutral'}>{t(`shortfall.action.${a.kind}` as TranslationKey)}</Badge>
                      <span className="text-body-sm font-medium">
                        {a.decision === 'SALARY_PERCENTAGE'
                          ? t('shortfall.decision.salary', { percent: a.deductionPercent ?? 0 })
                          : a.decision === 'EXCUSED'
                            ? t('shortfall.decision.excused')
                            : t('shortfall.decision.leave')}
                      </span>
                      {a.amount != null && a.currency && (
                        <span className="tabular text-caption text-muted">≈ {formatMoney(a.amount, a.currency, locale)}</span>
                      )}
                    </div>
                    <p className="mt-0.5 text-caption text-muted">
                      {a.actor ? localized(a.actor) : '—'} · <span className="tabular">{formatDateTime(a.createdAt, locale)}</span>
                    </p>
                    {a.note && (
                      <p className="mt-1 whitespace-pre-wrap text-body-sm text-[var(--text-secondary)]" dir="auto">
                        {a.note}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>

        <div className="min-w-0 space-y-5">
          <Card>
            <CardHeader title={t('shortfall.context')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('nav.leaves')}>
                {leave.remaining != null ? (
                  <span className="tabular">
                    {t('shortfall.leaveBalance', { year: String(leave.year), remaining: formatNumber(leave.remaining, locale) })}
                  </span>
                ) : (
                  '—'
                )}
                {leave.pendingDays > 0 && (
                  <span className="block text-caption text-muted">{t('shortfall.leavePending', { n: formatNumber(leave.pendingDays, locale) })}</span>
                )}
              </Detail>
              <Detail label={t('rewards.month')}>
                <span className="tabular">{monthLabel(row.options.payroll.effectiveMonth, locale)}</span>
                {row.options.payroll.status && (
                  <span className="ms-1.5">
                    <Badge tone={statusTone(row.options.payroll.status)}>{enumLabel(row.options.payroll.status, locale)}</Badge>
                  </span>
                )}
              </Detail>
              {row.options.salary?.dailyRate != null && (
                <Detail label={t('shortfall.dayRate')}>
                  <span className="whitespace-nowrap tabular">{formatMoney(row.options.salary.dailyRate, row.options.salary.currency, locale)}</span>
                </Detail>
              )}
            </dl>
          </Card>
        </div>
      </div>
    </>
  );
}
